/**
 * Consent-gated analytics loader.
 *
 * The site had no analytics of any kind, and a cookie banner that gated
 * nothing (docs/AUDIT.md §6). Under UK PECR no non-essential tag may load
 * before consent, so nothing here runs until loadAnalytics() is called, and it
 * is only called from the two consent paths:
 *
 *   - CookieBanner, when the visitor accepts
 *   - gatsby-browser onClientEntry, when consent is already stored
 *
 * Gatsby only exposes browser env vars prefixed GATSBY_, and both IDs are
 * optional: with neither set every function here is a no-op, so the site
 * behaves exactly as it does today until the IDs are configured in Netlify.
 */

export const CONSENT_COOKIE = "cookie-accept-ufs"
export const CONSENT_ACCEPTED = "active"
export const CONSENT_REJECTED = "rejected"

// One year. The cookie previously had no expiry at all, making it a session
// cookie that re-prompted on every new browser session.
export const CONSENT_MAX_AGE = 60 * 60 * 24 * 365

const GTM_ID = process.env.GATSBY_GTM_ID
const GA4_ID = process.env.GATSBY_GA4_ID

let loaded = false

const injectScript = (src, { async = true } = {}) => {
  const script = document.createElement("script")
  script.async = async
  script.src = src
  document.head.appendChild(script)
  return script
}

const loadGtm = () => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  })
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`)
}

const loadGa4 = () => {
  window.dataLayer = window.dataLayer || []
  // Must be a real function declaration, not an arrow: gtag relies on
  // `arguments` being pushed to dataLayer verbatim.
  function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag = gtag

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`)
  gtag("js", new Date())
  gtag("config", GA4_ID)
}

/**
 * Injects the configured tags. Safe to call more than once — the second call
 * is a no-op, which matters because both consent paths can fire in one session.
 */
export const loadAnalytics = () => {
  if (loaded) return false
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false
  }
  if (!GTM_ID && !GA4_ID) return false

  if (GTM_ID) loadGtm()
  if (GA4_ID) loadGa4()

  loaded = true
  return true
}

/**
 * Reports a conversion. For this business the leads are form submissions,
 * tel: clicks and mailto: clicks. No-ops when nothing is loaded, so call sites
 * do not need their own guards.
 */
export const trackEvent = (name, params = {}) => {
  if (typeof window === "undefined") return
  if (typeof window.gtag === "function") {
    window.gtag("event", name, params)
  } else if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: name, ...params })
  }
}

export const hasConsent = value => value === CONSENT_ACCEPTED
